'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import {
  Play, SkipBack, SkipForward, Shuffle, Repeat, Heart, MoreHorizontal,
  Volume2, Volume1, VolumeX, ListMusic, Maximize2, Mic2, PictureInPicture2,
  ChevronDown, X, Cast,
} from 'lucide-react'
import BaffleConnect from './BaffleConnect'
import Artwork from './Artwork'
import Marquee from './Marquee'
import ArtistLink from './ArtistLink'
import DownloadButton from './DownloadButton'
import MiniPlayerPipPortal, { useMiniPlayerPip } from './MiniPlayerPip'
import QueueDrawer from './QueueDrawer'
import { useTrackMenu } from './TrackMenu'
import { BigPlayButton, CtrlIcon, RangeSlider, RepeatOneBadge } from './lecteur/ui'
import { cx } from './ui/kit'
import { fmtTime, usePlayer, usePlayerPosition } from '@/lib/player-context'
import { useAlbum } from '@/lib/artwork'
import { useFavorites, useRecents } from '@/lib/library'
import { useCoverOverride } from '@/lib/cover-overrides'
import { useParty } from '@/lib/party-context'
import { logHistory } from '@/lib/history'

/**
 * Largeur RÉELLE de la barre latérale (fixe) : 256 px en desktop, 78 px en
 * tablette portrait, 0 si elle est repliée / masquée (mobile, TV). La PlayerBar
 * desktop commence juste à sa droite pour ne jamais la recouvrir.
 * `null` tant qu'on n'a pas mesuré → on retombe sur les classes CSS.
 */
export function useSidebarWidth() {
  const [w, setW] = useState<number | null>(null)
  useEffect(() => {
    let ro: ResizeObserver | null = null
    let el: Element | null = null
    const measure = () => {
      const e = document.querySelector<HTMLElement>('.blc-sidebar')
      setW(e ? Math.round(e.getBoundingClientRect().width) : 0)
    }
    const attach = () => {
      const e = document.querySelector('.blc-sidebar')
      if (e && e !== el) {
        ro?.disconnect()
        el = e
        ro = new ResizeObserver(measure)
        ro.observe(e)
      }
      measure()
    }
    attach()
    // Filet de sécurité : la barre peut être repliée / remontée après coup.
    const iv = setInterval(attach, 1500)
    window.addEventListener('resize', measure)
    return () => { ro?.disconnect(); clearInterval(iv); window.removeEventListener('resize', measure) }
  }, [])
  return w
}

export default function PlayerBar() {
  const {
    current, playing, duration,
    toggle, next, prev, seek, stop,
    volume, setVolume,
    setVideoFullscreen,
    repeat, shuffle, cycleRepeat, toggleShuffle,
  } = usePlayer()
  const { has, toggle: toggleFav } = useFavorites()
  const { push: pushRecent } = useRecents()
  const coverOverride = useCoverOverride(current?.videoId)
  const position = usePlayerPosition()
  const album = useAlbum(current)
  const lastTracked = useRef<string | null>(null)
  const pathname = usePathname()
  const router = useRouter()
  const trackMenu = useTrackMenu()
  const sideW = useSidebarWidth()

  const { open: openMiniPip, close: closeMiniPip, pipWin, isOpen: pipOpen } = useMiniPlayerPip()
  const { role, playTarget } = useParty()
  const [queueOpen, setQueueOpen] = useState(false)
  // FAB fusionné (état réduit) : 1 bouton rond → 2 actions (baffle + afficher lecteur).
  const [fabOpen, setFabOpen] = useState(false)

  // État RÉDUIT : la PlayerBar peut être collapsed en un mini-bouton flottant.
  // Persisté en localStorage pour que ça reste réduit entre les pages.
  const [collapsed, setCollapsed] = useState(false)
  // Une nouvelle visite affiche toujours la barre complète.
  useEffect(() => {
    if (!current?.videoId) return
    setCollapsed(false)
    try { localStorage.setItem('blc.player.collapsed', '0') } catch {}
  }, [current?.videoId])
  const toggleCollapsed = () => {
    setCollapsed(c => {
      const next = !c
      try { localStorage.setItem('blc.player.collapsed', next ? '1' : '0') } catch {}
      return next
    })
  }
  // Hauteur RÉELLE de la barre d'onglets mobile → la bulle se pose AU-DESSUS
  // (en bas à droite), sans chevaucher le dernier bouton (« Plus »).
  const [navH, setNavH] = useState(0)
  useEffect(() => {
    const nav = document.querySelector<HTMLElement>('nav[aria-label="Navigation principale"]')
    if (!nav) return
    const upd = () => setNavH(getComputedStyle(nav).display === 'none' ? 0 : nav.offsetHeight)
    upd()
    const ro = new ResizeObserver(upd); ro.observe(nav)
    window.addEventListener('resize', upd)
    return () => { ro.disconnect(); window.removeEventListener('resize', upd) }
  }, [pathname])

  // Pose un data-attribute sur <html> que YoutubePlayer regarde pour cacher
  // l'iframe (sinon elle reste visible par-dessus le mini-bouton quand on réduit).
  useEffect(() => {
    if (typeof document === 'undefined') return
    document.documentElement.dataset.blcPlayerCollapsed = collapsed ? '1' : '0'
    // Notifie l'iframe pour qu'elle se replace immédiatement (event bubblé)
    window.dispatchEvent(new Event('resize'))
  }, [collapsed])

  // Pulse "tap pour agrandir" : démarre à chaque changement de piste,
  // s'arrête au bout de 4s.
  const [hintExpand, setHintExpand] = useState(false)
  useEffect(() => {
    if (current && playing && lastTracked.current !== current.videoId) {
      lastTracked.current = current.videoId
      pushRecent(current)
      logHistory(current)   // journal complet (date/heure, répétitions conservées)
      // Lancer une musique AFFICHE TOUJOURS la barre (même si on l'avait réduite).
      setCollapsed(false)
      try { localStorage.setItem('blc.player.collapsed', '0') } catch {}
      setHintExpand(true)
      const t = setTimeout(() => setHintExpand(false), 4000)
      return () => clearTimeout(t)
    }
  }, [current, playing, pushRecent])

  // Couper / rétablir le son depuis l'icône volume.
  const lastVol = useRef<number>(volume || 70)
  useEffect(() => { if (volume > 0) lastVol.current = volume }, [volume])
  const toggleMute = () => setVolume(volume === 0 ? (lastVol.current || 70) : 0)
  const VolIcon = volume === 0 ? VolumeX : volume < 50 ? Volume1 : Volume2

  const pct = duration > 0 ? (position / duration) * 100 : 0
  const hasTrack = !!current
  const isFav = current ? has(current.videoId) : false
  // Masquée si pas de piste, sur le lecteur plein écran, si on est HÔTE (la
  // BaffleBar prend le relais), ou si on est invité avec destination « baffle ».
  // L'invité qui choisit « téléphone » garde son lecteur du bas local.
  const hideBar = pathname === '/app/player' || pathname === '/app/karaoke'
    || (role === 'guest' && playTarget === 'baffle')

  // Hauteur visible de la barre → variable CSS `--blc-playerbar-h` sur <html>
  // (utilisée par les toasts et le bouton Télécommande pour se placer au-dessus).
  const barRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--blc-bubble-h', !hideBar && collapsed ? '72px' : '0px')
    if (hideBar || collapsed) { root.style.setProperty('--blc-playerbar-h', '0px'); return }
    const el = barRef.current
    const upd = () => root.style.setProperty('--blc-playerbar-h', `${el ? el.offsetHeight + 8 : 0}px`)
    upd()
    const ro = el ? new ResizeObserver(upd) : null
    if (el && ro) ro.observe(el)
    return () => { ro?.disconnect() }
  }, [hideBar, collapsed, hasTrack])
  useEffect(() => () => { document.documentElement.style.setProperty('--blc-playerbar-h', '0px') }, [])

  // On rend toujours le portail PiP (même quand la barre est masquée),
  // sinon la fenêtre PiP perd son contenu en navigation.
  const pipPortal = (
    <MiniPlayerPipPortal pipWin={pipWin} onClose={closeMiniPip} />
  )

  if (hideBar) return pipPortal

  const cover = coverOverride || current?.thumbnail
  const openMenu = (e: React.MouseEvent<HTMLButtonElement>) => { if (current) trackMenu(current).onContextMenu(e) }

  return (
    <>
    {pipPortal}
    <QueueDrawer open={queueOpen} onClose={() => setQueueOpen(false)} />

    {/* ============== LECTEUR RÉDUIT — groupe de pastilles (bas-droite) =========
        IDLE : UNE pastille = jaquette + pictogramme CAST.
        AU CLIC : elle se déplie en 2 boutons de MÊME TAILLE — « Lecteur »
        (jaquette + play) juste AU-DESSUS de « Connexion baffle » — plus un
        petit bouton pour FERMER complètement. */}
    <div
      className="fixed z-50 flex flex-col items-end gap-2.5 pointer-events-none"
      style={{
        bottom: navH ? navH + 16 : 20,
        right: navH ? 14 : 20,
        transform: collapsed ? 'scale(1)' : 'scale(0)',
        opacity: collapsed ? 1 : 0,
        pointerEvents: collapsed ? 'auto' : 'none',
        transition: 'transform 380ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 280ms ease',
        willChange: 'transform, opacity',
      }}
    >
      {/* BULLE : un toucher → la barre de lecture réapparaît. */}
      {collapsed && <button
        onClick={toggleCollapsed}
        title="Afficher le lecteur" aria-label="Afficher le lecteur"
        className="relative w-14 h-14 rounded-full overflow-hidden active:scale-95 border border-a3/60 shadow-[0_8px_24px_rgb(var(--a2)/.5)]"
      >
        {current && !current.isVideo
          ? <span className="absolute inset-0" style={{ filter: 'brightness(0.6)' }}><Artwork track={current} fill strict size={56} rounded="rounded-full" /></span>
          : cover
            ? <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'brightness(0.6)' }} draggable={false} />
            : <span className="absolute inset-0 bg-gradient-to-br from-a2 to-a1" />}
        {playing && <span className="absolute inset-0 rounded-full ring-2 ring-a3/80 animate-ping" style={{ animationDuration: '1.8s' }} />}
        <span className="absolute inset-0 flex items-center justify-center text-white">
          {playing
            ? <span className="flex items-end gap-[3px] h-4">{[0, 1, 2].map(k => <span key={k} className="w-[3px] h-full bg-white rounded-sm origin-bottom animate-equalizer" style={{ animationDelay: `${k * 0.18}s` }} />)}</span>
            : <Play size={20} className="ml-0.5 fill-white drop-shadow" />}
        </span>
      </button>}
    </div>

    {/* ============== PLAYERBAR (étendue) ==============
        z-50 au-dessus de l'iframe YT en z-45. Desktop : pleine largeur À DROITE
        de la barre latérale. Mobile : carte compacte au-dessus des onglets. */}
    <div
      data-tvdock="1"
      role="region"
      aria-label="Lecteur de musique"
      aria-hidden={collapsed}
      ref={el => { if (el) el.inert = collapsed }}
      className={cx('blc-playerbar fixed right-0 z-50 pointer-events-none', sideW == null && 'left-0 md:left-64 md:portrait:left-[78px]')}
      style={{
        left: sideW == null ? undefined : sideW,
        bottom: 'var(--player-bottom, 0px)',
        transform: collapsed ? 'translateY(115%)' : 'translateY(0)',
        opacity: collapsed ? 0 : 1,
        pointerEvents: collapsed ? 'none' : 'auto',
        transition: 'transform 420ms cubic-bezier(0.32, 0.72, 0.24, 1), opacity 300ms ease, left 220ms ease',
        willChange: 'transform, opacity',
      }}
    >
      <div ref={barRef} className="pointer-events-auto relative">

        {/* ============ MOBILE : mini-lecteur COMPACT posé au-dessus de la barre
            d'onglets. Tap sur la pochette/titre → lecteur plein écran. ============ */}
        <div className="md:hidden px-2 pb-2">
          <div className="lg-surface lg-bar relative overflow-hidden rounded-2xl border border-white/[0.08] shadow-[0_10px_30px_rgba(0,0,0,.5)]">
            <div className="flex items-center gap-3 pl-2 pr-2 py-2">
              <Link href={hasTrack ? "/app/player" : "/app/search"} className="flex items-center gap-3 min-w-0 flex-1" title="Ouvrir le lecteur">
                <span className="relative w-12 h-12 rounded-xl overflow-hidden shrink-0 ring-1 ring-white/10">
                  <Artwork track={current} size={56} strict rounded="rounded-xl" className="w-full h-full" />
                </span>
                <span className="min-w-0 flex-1 leading-tight">
                  <Marquee className="text-[14.5px] font-bold text-white">{current?.title || 'Choisissez un titre'}</Marquee>
                  <span className="block text-[12.5px] text-white/55 truncate mt-0.5">
                    {current?.artist || '—'}{album ? ` · ${album}` : ''}
                  </span>
                </span>
              </Link>
              <button onClick={() => current && toggleFav(current)} aria-label={isFav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                className="shrink-0 w-9 h-9 grid place-items-center text-a4 active:scale-90 transition">
                <Heart size={20} className={isFav ? 'fill-a3 text-a3' : ''} />
              </button>
              <BigPlayButton playing={playing} onClick={hasTrack ? toggle : () => router.push("/app/search")} title={hasTrack ? undefined : "Choisir un titre"} size={42} />
              <button disabled={!hasTrack} onClick={next} aria-label="Morceau suivant"
                className="shrink-0 w-9 h-9 grid place-items-center text-white/85 hover:text-white active:scale-90 transition">
                <SkipForward size={22} className="fill-white/85" />
              </button>
              <button onClick={toggleCollapsed} aria-label="Réduire le lecteur" title="Réduire le lecteur"
                className="shrink-0 w-7 h-7 rounded-full grid place-items-center text-white/55 hover:text-white bg-white/[0.06] active:scale-90 transition">
                <ChevronDown size={15} />
              </button>
            </div>
            {/* Progression fine en bas de la carte */}
            <div className="absolute left-3 right-3 bottom-0 h-[3px] rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-a1 to-a3" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>

        {/* ============ TABLETTE / DESKTOP : barre complète pleine largeur ============ */}
        <div className="lg-surface lg-bar hidden md:block border-t border-white/[0.07]">
          {/* Colonnes : gauche/droite élastiques, centre de largeur fixe en
              tablette (≥ 512 px utiles avec la barre latérale) puis fluide. */}
          <div className="flex items-center gap-4 lg:gap-6 px-4 lg:px-6 py-3">

            {/* ---- GAUCHE : pochette + titre/artiste + cœur + ··· ---- */}
            <div className="flex items-center gap-3 flex-1 min-w-[64px]">
              {/* Jaquette CLIQUABLE → ouvre le lecteur plein écran.
                  L'iframe YT vient se poser dessus (yt-slot) ; grâce au z-50 du
                  wrapper > z-45 de l'iframe, c'est le Link qui reçoit les clics. */}
              <Link href={hasTrack ? "/app/player" : "/app/search"}
                id="yt-slot"
                data-yt-slot
                className="relative w-16 h-16 rounded-xl overflow-hidden shrink-0 block group/cover ring-1 ring-white/10 shadow-[0_6px_18px_rgba(0,0,0,.45)]"
                title="Ouvrir le lecteur plein écran"
                aria-label="Ouvrir le lecteur plein écran"
              >
                <div className="absolute inset-0">
                  <Artwork track={current} size={96} strict rounded="rounded-xl" className="w-full h-full" />
                </div>
                <div className="absolute inset-0 z-[1] bg-black/0 group-hover/cover:bg-black/45 transition flex items-center justify-center pointer-events-none">
                  <Maximize2 size={20} className="text-white opacity-0 group-hover/cover:opacity-100 drop-shadow-lg transition" />
                </div>
                {hintExpand && (
                  <span className="absolute inset-0 rounded-xl ring-2 ring-a3 animate-ping pointer-events-none z-[3]" />
                )}
              </Link>

              <div className="min-w-0 flex-1 leading-tight">
                <Link href={hasTrack ? "/app/player" : "/app/search"} className="block" title="Ouvrir le lecteur">
                  <Marquee className="text-[15px] font-bold text-white">
                    {current?.title || 'Choisissez un titre'}
                  </Marquee>
                </Link>
                {current?.artist ? (
                  <ArtistLink artist={current.artist} className="block mt-0.5 text-[13px] text-white/55 hover:text-white/85 transition">
                    <Marquee className="text-[13px] text-white/55">{current.artist}</Marquee>
                  </ArtistLink>
                ) : (
                  <div className="text-[13px] text-white/55 mt-0.5">&nbsp;</div>
                )}
              </div>

              <CtrlIcon disabled={!hasTrack} onClick={() => current && toggleFav(current)} title={isFav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                className="w-9 h-9 !text-a4 hover:!text-a5">
                <Heart size={19} className={isFav ? 'fill-a3 text-a3' : ''} />
              </CtrlIcon>
              <CtrlIcon disabled={!hasTrack} onClick={openMenu} title="Plus d'options (playlist, télécharger, vidéo plein écran…)" className="w-9 h-9">
                <MoreHorizontal size={20} />
              </CtrlIcon>
            </div>

            {/* ---- CENTRE : transport + progression ---- */}
            <div className="flex flex-col items-center gap-1.5 shrink-0 w-[236px] lg:w-auto lg:flex-[1.3] lg:min-w-[236px] lg:max-w-[660px]">
              <div className="flex items-center gap-2 lg:gap-4 xl:gap-5">
                <CtrlIcon onClick={toggleShuffle} active={shuffle} title={shuffle ? 'Aléatoire activé' : 'Aléatoire'} className="w-9 h-9">
                  <Shuffle size={18} />
                </CtrlIcon>
                <CtrlIcon disabled={!hasTrack} onClick={prev} title="Précédent" className="w-10 h-10 !text-white/90 hover:!text-white">
                  <SkipBack size={22} className="fill-current" />
                </CtrlIcon>
                <BigPlayButton playing={playing} onClick={hasTrack ? toggle : () => router.push("/app/search")} title={hasTrack ? undefined : "Choisir un titre"} size={50} className={playing ? 'animate-pulseGlow' : ''} />
                <CtrlIcon disabled={!hasTrack} onClick={next} title="Suivant" className="w-10 h-10 !text-white/90 hover:!text-white">
                  <SkipForward size={22} className="fill-current" />
                </CtrlIcon>
                <CtrlIcon onClick={cycleRepeat} active={repeat !== 'off'}
                  title={`Répétition : ${repeat === 'off' ? 'désactivée' : repeat === 'all' ? 'tous' : 'un seul'}`}
                  className="w-9 h-9" badge={repeat === 'one' ? <RepeatOneBadge /> : undefined}>
                  <Repeat size={18} />
                </CtrlIcon>
              </div>
              <div className="w-full flex items-center gap-2 lg:gap-2.5 text-[11px] lg:text-[11.5px] text-white/50 tabular-nums">
                <span className="shrink-0 w-9 text-right">{fmtTime(position)}</span>
                <RangeSlider value={Math.floor(position)} max={Math.floor(duration)} pct={pct}
                  disabled={!hasTrack} onChange={seek} ariaLabel="Progression" className="flex-1 min-w-0" />
                <span className="shrink-0 w-9">{fmtTime(duration)}</span>
              </div>
            </div>

            {/* ---- DROITE (comme la maquette) : paroles · file · volume − curseur + · agrandir ---- */}
            <div className="flex-1 flex items-center justify-end gap-1 lg:gap-2">
              <Link href="/app/karaoke" title="Paroles" aria-label="Paroles"
                className="w-9 h-9 shrink-0 grid place-items-center rounded-full text-white/70 hover:text-white transition">
                <Mic2 size={18} />
              </Link>
              <CtrlIcon onClick={() => setQueueOpen(true)} active={queueOpen} title="File d'attente" className="w-9 h-9">
                <ListMusic size={19} />
              </CtrlIcon>
              <div className="hidden lg:flex items-center gap-1 w-[150px] xl:w-[180px] shrink-0">
                <CtrlIcon onClick={toggleMute} title={volume === 0 ? 'Activer le son' : 'Couper le son'} className="w-8 h-8">
                  <VolIcon size={17} />
                </CtrlIcon>
                <RangeSlider value={volume} max={100} pct={volume} onChange={setVolume} ariaLabel="Volume" height={4} className="flex-1" />
                <CtrlIcon onClick={() => setVolume(Math.min(100, volume + 10))} title="Monter le son" className="w-8 h-8">
                  <Volume2 size={17} />
                </CtrlIcon>
              </div>
              <CtrlIcon onClick={toggleMute} title={volume === 0 ? 'Activer le son' : 'Couper le son'} className="w-9 h-9 lg:hidden">
                <VolIcon size={18} />
              </CtrlIcon>
              {/* Les « deux flèches » : ouvrent le VRAI lecteur (plein écran). */}
              <Link href={hasTrack ? "/app/player" : "/app/search"} title="Ouvrir le lecteur" aria-label="Ouvrir le lecteur"
                className="w-9 h-9 shrink-0 grid place-items-center rounded-full text-white/80 hover:text-white hover:bg-white/[0.06] transition">
                <Maximize2 size={18} />
              </Link>
              <span className="w-px h-6 bg-white/10 mx-0.5 shrink-0" />
              <CtrlIcon onClick={pipOpen ? closeMiniPip : openMiniPip} active={pipOpen}
                title={pipOpen ? 'Fermer le mini-lecteur' : 'Mini-lecteur (arrière-plan)'} className="w-8 h-8 hidden 2xl:grid">
                <PictureInPicture2 size={17} />
              </CtrlIcon>
              <CtrlIcon onClick={toggleCollapsed} title="Réduire en bulle" className="w-7 h-7 bg-white/[0.05]">
                <ChevronDown size={15} />
              </CtrlIcon>
              <CtrlIcon disabled={!hasTrack} onClick={stop} title="Fermer le lecteur (arrêter)" className="w-7 h-7 bg-white/[0.05]">
                <X size={15} />
              </CtrlIcon>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}
